import React, { useRef, useState } from 'react';
import { Image, Upload, X } from 'lucide-react';

const BackgroundImagePicker = ({
  label,
  description,
  presets = [],
  selectedId = '',
  selectedLabel = '',
  selectedPreview = '',
  onSelectPreset,
  onUploadImage,
  onClear,
  uploadLabel = 'Upload image',
  replaceLabel = 'Replace image',
  clearLabel = 'Remove background image',
  emptyLabel = 'No background image selected'
}) => {
  const inputRef = useRef(null);
  const [feedback, setFeedback] = useState('');

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file && !file.type?.startsWith('image/')) {
      setFeedback('Please choose a valid image file.');
      event.target.value = '';
      return;
    }

    if (file && onUploadImage) {
      setFeedback('');
      onUploadImage(file);
    }
    event.target.value = '';
  };

  const uploadButtonLabel = selectedPreview ? replaceLabel : uploadLabel;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-700">{label}</p>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
        <span className="text-[11px] uppercase tracking-wide text-gray-500">Image backgrounds</span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {presets.map((preset) => {
          const isSelected = selectedId === preset.key;
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => onSelectPreset?.(preset)}
              aria-pressed={isSelected}
              aria-label={`Use ${preset.name} background`}
              className={`group overflow-hidden rounded-xl border-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
              }`}
              title={preset.name}
            >
              <div className="relative aspect-video bg-gray-100">
                <img
                  src={preset.preview || preset.value}
                  alt={preset.name}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 bg-black/45 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                  {preset.name}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleUploadClick}
          aria-label={uploadButtonLabel}
          className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-400 hover:bg-gray-50"
        >
          <Upload size={16} />
          {uploadButtonLabel}
        </button>
        {selectedPreview && onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label={clearLabel}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <X size={16} />
            {clearLabel}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        aria-label={uploadButtonLabel}
      />

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3" aria-live="polite">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-20 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
            {selectedPreview ? (
              <img src={selectedPreview} alt={selectedLabel || label} className="h-full w-full object-cover" />
            ) : (
              <Image size={16} className="text-gray-400" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Selected</p>
            <p className="truncate text-sm font-semibold text-gray-800">{selectedLabel || emptyLabel}</p>
          </div>
        </div>
        {feedback && (
          <p className="mt-2 text-xs font-semibold text-red-600" role="alert">
            {feedback}
          </p>
        )}
      </div>
    </div>
  );
};

export default BackgroundImagePicker;
